import { CapacitorHttp } from '@capacitor/core';
import { isCapacitor } from './googleAuthService';
import { secureStorage } from './secureStorage';
import { getStepUpToken, setStepUpToken, promptStepUp, isStepUpRequired, ALL_PURPOSES } from './stepUp';

// ─── Global slow-call overlay ────────────────────────────────────────────────
// Shows the branded loading overlay after 600ms of any pending API call.
// Dispatches api:slow-start / api:slow-end so GlobalLoadingOverlay can react.
let _pendingCount = 0;
let _slowTimer = null;
let _overlayShowing = false;
// A request that never settles never reaches its finally, so the count stays
// above zero and the overlay would sit there for good. This caps how long it
// can cover the app regardless; the call itself is left alone to finish or fail
// on its own terms.
const OVERLAY_MAX_MS = 20000;
let _overlayWatchdog = null;

function _hideOverlay() {
  clearTimeout(_overlayWatchdog);
  _overlayWatchdog = null;
  if (!_overlayShowing) return;
  _overlayShowing = false;
  window.dispatchEvent(new CustomEvent('api:slow-end'));
}

function _trackStart(silent) {
  if (silent) return;
  _pendingCount++;
  if (_pendingCount === 1 && !_overlayShowing) {
    _slowTimer = setTimeout(() => {
      if (_pendingCount > 0) {
        _overlayShowing = true;
        window.dispatchEvent(new CustomEvent('api:slow-start'));
        _overlayWatchdog = setTimeout(_hideOverlay, OVERLAY_MAX_MS);
      }
    }, 600);
  }
}

function _trackEnd(silent) {
  if (silent) return;
  _pendingCount = Math.max(0, _pendingCount - 1);
  if (_pendingCount === 0) {
    clearTimeout(_slowTimer);
    _hideOverlay();
  }
}
// ────────────────────────────────────────────────────────────────────────────

// On mobile (Capacitor), relative paths have no origin — REACT_APP_MOBILE_BACKEND_DOMAIN
// must be set via the CI environment variable MOBILE_BACKEND_DOMAIN. In a browser,
// this must stay relative (same-origin) even when that env var is set to something
// else (e.g. a LAN IP for testing the native app) — a browser page fetching an
// absolute URL to a different host/IP than its own is cross-origin and gets CORS-
// blocked, even if it's literally the same dev server. Relative paths always resolve
// against the page's own origin, which is correct in a browser regardless of what
// REACT_APP_MOBILE_BACKEND_DOMAIN happens to be set to for native's sake.
const BACKEND_DOMAIN = isCapacitor ? (import.meta.env.REACT_APP_MOBILE_BACKEND_DOMAIN || '') : '';
const getAPIURL = (api_relative_path) =>
  BACKEND_DOMAIN + import.meta.env.REACT_APP_EXPORTS_API_URL + api_relative_path;

// ─── Server reachability ────────────────────────────────────────────────────
// Rules:
//  1. Health check runs on a fixed schedule — NEVER triggered by apiCall
//     failures, so sign-in blips or any single bad request can't cause it.
//  2. 2 consecutive health-check failures required before showing the banner.
//  3. navigator.onLine / 'offline' event is the fast path for true offline.
//  4. Once _unreachable is true, all apiCalls short-circuit silently.
let _unreachable    = false;
let _healthPending  = false;
let _healthFailures = 0;
const HEALTH_THRESHOLD = 2;

async function _checkHealth() {
  if (_healthPending) return;
  _healthPending = true;
  try {
    await fetch(getAPIURL('/health'), { cache: 'no-store', signal: AbortSignal.timeout(4000) });
    _healthFailures = 0;
    if (_unreachable) {
      _unreachable = false;
      window.dispatchEvent(new CustomEvent('server:reachable'));
    }
  } catch (err) {
    // Only ERR_CONNECTION_REFUSED (TypeError) means the server is truly down.
    // AbortError = timeout (server slow/hung — not the same as unreachable).
    if (err instanceof TypeError) {
      _healthFailures++;
      if (_healthFailures >= HEALTH_THRESHOLD && !_unreachable) {
        _unreachable = true;
        window.dispatchEvent(new CustomEvent('server:unreachable'));
      } else if (_healthFailures === 1) {
        // First failure — retry in 5s instead of waiting 30s
        setTimeout(_checkHealth, 5000);
      }
    }
  } finally {
    _healthPending = false;
  }
}

// Proactive: check on load (after 3 s), then every 30 s
setTimeout(() => {
  _checkHealth();
  setInterval(_checkHealth, 30000);
}, 3000);

// Fast path: browser going offline / coming back online
window.addEventListener('offline', () => {
  _healthFailures = HEALTH_THRESHOLD;
  if (!_unreachable) {
    _unreachable = true;
    window.dispatchEvent(new CustomEvent('server:unreachable'));
  }
});
window.addEventListener('online', () => {
  _healthFailures = 0;
  _checkHealth();
});
// ────────────────────────────────────────────────────────────────────────────

// ─── Session-expiry redirect ────────────────────────────────────────────────
// A hard navigation (window.location.href) cancels every other in-flight fetch
// on the page — poll, invitations/mine, stream token, whatever else happens to
// be running. Those cancellations are collateral, not real failures, so once
// __sessionRedirecting is set, apiCall/apiUpload/apiImageCall's own catch blocks
// and the global fetch/console.error interceptors in index.jsx skip logging them.
//
// Logging the redirect itself happens BEFORE the flag is set, so this one
// deliberate event still reaches Grafana — otherwise there'd be zero visibility
// left into how often sessions actually expire (the "Session expired" throw
// itself was already excluded from logging before this flag existed).
//
// The flag self-clears after SESSION_REDIRECT_WINDOW_MS as a safety net: if a
// future change ever makes this redirect delayed or conditional, a stuck
// `true` would silently swallow all error logging for the rest of the tab's
// life. 3s was too tight in practice — slow mobile connections (observed on
// mobile Safari) can take 4s+ to actually abort the collateral in-flight
// requests, so those aborts landed after the flag had already cleared and
// got logged as false-positive errors.
const SESSION_REDIRECT_WINDOW_MS = 10000;
function _redirectToSignin(fromPath) {
  console.error('[Session] Expired — redirecting to signin', { from: fromPath });
  window.__sessionRedirecting = true;
  setTimeout(() => { window.__sessionRedirecting = false; }, SESSION_REDIRECT_WINDOW_MS);
  window.location.href = '/signin?expired=true';
}
// ────────────────────────────────────────────────────────────────────────────

// ─── Sliding session refresh ────────────────────────────────────────────────
// The JWT's expiry is fixed from login time, not from last activity — someone
// who logs in and comes back mid-use a day+ later hits a real, correctly-timed
// expiry that nonetheless reads as "kicked out during a session that should've
// been valid." Piggybacking on whatever request is already in flight, reissue
// the token once less than half its life remains, so an actively-used session
// never approaches its own expiry. A fully idle tab still expires normally —
// this only extends sessions with real ongoing activity.
// Native (Capacitor) already uses a 30-day token and isn't affected by this.
const REFRESH_THRESHOLD_MS = 12 * 60 * 60 * 1000;
let _refreshInFlight = null;

function _decodeTokenExpMs(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function _maybeRefreshToken(token) {
  if (_refreshInFlight) return;
  const expMs = _decodeTokenExpMs(token);
  if (!expMs || expMs - Date.now() > REFRESH_THRESHOLD_MS) return;

  _refreshInFlight = fetch(getAPIURL('/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({}),
    cache: 'no-store',
  })
    .then(res => (res.ok ? res.json() : null))
    .then(data => {
      if (data?.token) return secureStorage.setItem('token', data.token);
    })
    .catch(() => {
      // Silent — worst case the token just expires normally later, same as before this existed.
    })
    .finally(() => {
      _refreshInFlight = null;
    });
}
// ────────────────────────────────────────────────────────────────────────────

export const apiCall = async (endpoint, options = {}) => {
  return _apiCall(endpoint, options, false);
};

/**
 * @param {boolean} isStepUpRetry guards against looping: a retry that is itself
 *   answered with 428 gives up rather than prompting for a code again.
 */
const _apiCall = async (endpoint, options = {}, isStepUpRetry = false, stepUpToken = null) => {
  // Short-circuit immediately — no network request, no error noise
  if (_unreachable) {
    const err = new Error('Server unreachable');
    err.serverUnreachable = true;
    throw err;
  }

  const url = getAPIURL(endpoint);
  const token = await secureStorage.getItem('token');

  if (token && !isCapacitor && !endpoint.startsWith('/auth/')) {
    _maybeRefreshToken(token);
  }

  const headers = {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Send any step-up token we already hold. Harmless on endpoints that don't
  // check it, and it means a second sensitive action inside the token's life
  // doesn't re-prompt.
  // A token handed straight to us by the prompt wins. Looking it up in storage
  // instead would make this depend on StepUpGate having written it first — a
  // side effect, invisible from here, that silently drops the header if it ever
  // doesn't happen.
  if (stepUpToken) {
    headers['x-step-up-token'] = stepUpToken;
  } else if (options.stepUpPurpose) {
    const stored = getStepUpToken(options.stepUpPurpose);
    if (stored) headers['x-step-up-token'] = stored;
  } else {
    for (const purpose of ALL_PURPOSES) {
      const t = getStepUpToken(purpose);
      if (t) { headers['x-step-up-token'] = t; break; }
    }
  }

  const method = (options.method || 'GET').toUpperCase();
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  _trackStart(options.silent);
  // The step-up prompt waits on a human, which can be a minute. The loading
  // overlay must come down for that, but the finally below still runs on the
  // way out — so release once, and record it, or the pending count drifts
  // negative and the overlay for the NEXT call gets torn down early.
  let overlayReleased = false;
  const releaseOverlay = () => {
    if (overlayReleased) return;
    overlayReleased = true;
    _trackEnd(options.silent);
  };

  try {
    let response;

    if (isCapacitor) {
      // CapacitorHttp makes native HTTP calls outside the WKWebView — no CORS restrictions.
      // fetch() inside WKWebView is blocked by WebKit's access-control checks for cross-origin
      // requests even when the server sends correct CORS headers.
      const nativeRes = await CapacitorHttp.request({
        method: options.method || 'GET',
        url,
        headers,
        data: options.body || undefined,
        responseType: 'json',
      });
      // Normalize to the subset of the fetch Response interface used below
      response = {
        ok: nativeRes.status >= 200 && nativeRes.status < 300,
        status: nativeRes.status,
        headers: { get: (k) => { const lower = k.toLowerCase(); const entry = Object.entries(nativeRes.headers || {}).find(([h]) => h.toLowerCase() === lower); return entry ? entry[1] : undefined; } },
        json: () => Promise.resolve(nativeRes.data),
      };
    } else {
      const config = {
        headers,
        cache: 'no-store',
        ...options,
      };
      response = await fetch(url, config);
    }

    // Handle 401 (token expired/invalid) - but NOT for auth endpoints
    const isAuthEndpoint = endpoint.startsWith('/auth/');
    if (response.status === 401 && !isAuthEndpoint) {
      await secureStorage.removeItem('token');
      await secureStorage.removeItem('socialUser');
      // Skip redirect during intentional logout — window.__loggingOut is set by AuthContext.logout()
      // to prevent the 401 from disableNativePush triggering a full WebView reload (visible as crash)
      const currentPath = window.location.pathname + window.location.search;
      const authPaths = ['/signin', '/signup', '/claim-account', '/forgot-password'];
      const alreadyOnAuthPage = authPaths.some(p => currentPath.startsWith(p));
      if (!alreadyOnAuthPage && !window.__loggingOut) {
        localStorage.setItem('postLoginRedirect', currentPath);
        _redirectToSignin(currentPath);
      }
      throw new Error('Session expired');
    }

    // The server wants the user to prove it's really them. Prompt, then replay
    // the identical request once with the token. Deliberately ahead of the
    // generic !ok branch so it never surfaces as a raw error toast.
    if (response.status === 428 && !isStepUpRetry) {
      const stepUpData = await response.json().catch(() => ({}));
      if (isStepUpRequired(response.status, stepUpData)) {
        releaseOverlay();
        const fresh = await promptStepUp(stepUpData.purpose);
        if (!fresh) {
          const cancelled = new Error('Verification cancelled');
          cancelled.stepUpCancelled = true;
          throw cancelled;
        }
        return _apiCall(endpoint, { ...options, stepUpPurpose: stepUpData.purpose }, true, fresh);
      }
      const error = new Error(stepUpData.message || 'Precondition required');
      error.status = 428;
      error.data = stepUpData;
      throw error;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Handle empty responses (204 No Content, DELETE, etc.)
    //
    // The Capacitor path already told the native HTTP layer to parse the body
    // as JSON (responseType: 'json' above), so nativeRes.data is already the
    // real parsed value — trust it directly. iOS's native HTTP layer doesn't
    // reliably surface the content-type header back through CapacitorHttp's
    // headers object (documented Capacitor/iOS quirk), so sniffing it here
    // would silently replace real array/object responses with { success: true }
    // whenever the lookup came back empty — this is exactly what corrupted
    // sport-type lists into a non-array value and crashed "X.map is not a
    // function" on iOS only, never on web (browser fetch() reports content-type
    // reliably).
    if (isCapacitor) {
      if (response.status === 204) {
        return { success: true };
      }
    } else {
      const contentType = response.headers.get('content-type');
      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        return { success: true };
      }
    }

    const data = await response.json();
    // Notify idle tracker that user is active (successful API call resets the idle timer)
    document.dispatchEvent(new Event('api-activity'));
    return data;
  } catch (error) {
    // ERR_CONNECTION_REFUSED → server is truly down; trigger health check immediately
    // so the banner appears in ~5s instead of waiting up to 30s for the scheduled check.
    // TypeError = connection refused or network offline (not a 4xx/5xx HTTP error).
    if (error instanceof TypeError && !_unreachable && !window.__sessionRedirecting) {
      _checkHealth();
    }
    if (!options.silent && !error?.serverUnreachable) {
      // Session expired is already handled (redirect to signin) — skip Grafana noise
      const isSessionExpired = error?.message === 'Session expired';
      if (!error?.status && !isSessionExpired && !window.__sessionRedirecting) {
        console.error('API call failed:', error?.message || error, '| URL:', url);
      }
    }
    throw error;
  } finally {
    // Every exit — success, early return, thrown error — passes through here,
    // so the pending count cannot drift and strand the loading overlay.
    releaseOverlay();
  }
};

export default apiCall;

const getImageHandlerURL = (path) =>
  BACKEND_DOMAIN + import.meta.env.REACT_APP_IMAGE_HANDLER_CONTEXT_PATH + path;

/**
 * Multipart file upload utility for the image handler (exportsapi2).
 * Unlike apiCall, this does NOT set Content-Type so the browser sets the
 * multipart boundary automatically.
 *
 * @param {string} endpoint - The endpoint path (e.g., '/upload')
 * @param {FormData} formData - The multipart form data
 * @returns {Promise<object>} - The response data
 * @throws {Error} with status 422 for content policy violations
 */
export const apiUpload = async (endpoint, formData) => {
  const url = getImageHandlerURL(endpoint);
  const token = await secureStorage.getItem('token');

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      cache: 'no-store',
    });

    if (response.status === 401) {
      await secureStorage.removeItem('token');
      await secureStorage.removeItem('socialUser');
      const currentPath = window.location.pathname + window.location.search;
      const authPaths = ['/signin', '/signup', '/claim-account', '/forgot-password'];
      const alreadyOnAuthPage = authPaths.some(p => currentPath.startsWith(p));
      if (!alreadyOnAuthPage && !window.__loggingOut) {
        localStorage.setItem('postLoginRedirect', currentPath);
        _redirectToSignin(currentPath);
      }
      throw new Error('Session expired');
    }

    // CloudFront converts 403/404 from origin into "200 + index.html" for SPA routing.
    // Detect this before attempting JSON.parse so the error is clear, not a parse failure.
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('text/html')) {
      const err = new Error('The system is facing some issue. Please try later.');
      err.status = response.status;
      throw err;
    }

    let data;
    try {
      data = await response.json();
    } catch {
      // Non-JSON response (e.g. CloudFront HTML error page when payload is too large).
      const msg = response.status === 413
        ? 'This file is too large to upload. Please choose a smaller photo.'
        : 'The system is facing some issue. Please try later.';
      const err = new Error(msg);
      err.status = response.status;
      throw err;
    }

    if (response.status === 422) {
      const error = new Error(data.message || 'Content policy violation');
      error.status = 422;
      error.policy = data.policy;
      throw error;
    }

    if (!response.ok) {
      const error = new Error(data.message || `Upload failed (${response.status})`);
      error.status = response.status;
      throw error;
    }

    document.dispatchEvent(new Event('api-activity'));
    return data;
  } catch (error) {
    if (!window.__sessionRedirecting) {
      console.error('Upload failed:', error?.message || error, '| URL:', url);
    }
    throw error;
  }
};

/**
 * JSON POST/PATCH/etc to the image handler (exsportscoreapi).
 * Used for non-multipart endpoints like /move.
 */
export const apiImageCall = async (endpoint, options = {}) => {
  const url = getImageHandlerURL(endpoint);
  const token = await secureStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      ...options,
      headers,
      cache: 'no-store',
    });

    if (response.status === 401) {
      await secureStorage.removeItem('token');
      await secureStorage.removeItem('socialUser');
      const currentPath = window.location.pathname + window.location.search;
      const authPaths = ['/signin', '/signup', '/claim-account', '/forgot-password'];
      if (!authPaths.some(p => currentPath.startsWith(p)) && !window.__loggingOut) {
        _redirectToSignin(currentPath);
      }
      throw new Error('Session expired');
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.message || `Request failed (${response.status})`);
      error.status = response.status;
      throw error;
    }

    document.dispatchEvent(new Event('api-activity'));
    return data;
  } catch (error) {
    if (!window.__sessionRedirecting) {
      console.error('Image handler call failed:', error?.message || error, '| URL:', url);
    }
    throw error;
  }
};
